import { useEffect, useSyncExternalStore } from 'react'
import { authedFetch, getToken, setToken } from '@/lib/api'

type PermMap = Record<string, Record<string, boolean>>

interface PermissionSnapshot {
  perms: PermMap
  role: string
}

let snapshot: PermissionSnapshot = { perms: {}, role: '' }
const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function getSnapshot() {
  return snapshot
}

function publish(perms: PermMap, role: string) {
  snapshot = { perms, role }
  listeners.forEach(listener => listener())
}

export function initPermissions(perms: PermMap, role: string) {
  publish(perms ?? {}, role ?? '')
}

export function resetPermissions() {
  publish({}, '')
}

export function isPermissionsLoaded(): boolean {
  return snapshot.role !== ''
}

/** Hydrate the shared permission store for any authenticated application host. */
export function usePermissionsBootstrap(handoffToken?: string | null) {
  useEffect(() => {
    let cancelled = false
    resetPermissions()

    async function hydrate() {
      try {
        // Kiosk URLs can supply their JWT on the first load, before App's token
        // passthrough effect runs. Wait for the public async token contract
        // rather than depending on its current localStorage write ordering.
        if (!getToken() && handoffToken) {
          await setToken(handoffToken, { skipSideEffects: true })
        }

        if (!getToken()) return

        const response = await authedFetch('/api/v1/auth/me', {}, { redirectOnAuthFailure: false })
        const data = response.ok ? await response.json() : null
        if (!cancelled && data) initPermissions(data.permissions ?? {}, data.role ?? '')
      } catch {
        if (!cancelled) resetPermissions()
      }
    }

    void hydrate()

    return () => {
      cancelled = true
      resetPermissions()
    }
  }, [handoffToken])
}

export function usePermissions() {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  function can(resource: string, action = 'view'): boolean {
    if (current.role === 'admin') return true
    return current.perms[resource]?.[action] ?? false
  }
  return { can }
}
